import StatusChip from 'components/@extended/StatusChip';
import { PriorityHighRounded as PriorityHighRoundedIcon } from 'components/icons';
import { Tooltip } from 'components/ui/tooltip';
import useAuth from 'hooks/useAuth';
import { MatterListItem } from 'types/matters';
import { TimecardListItemData, TimecardStatusType } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { isMatterClosed } from 'utils/matterState';

type ClosedCaseDraftTimecard = Pick<TimecardListItemData, 'timecardStatusTypeCode'> & {
    employee: Pick<TimecardListItemData['employee'], 'id'>;
    createdByEmployee?: Pick<NonNullable<TimecardListItemData['createdByEmployee']>, 'id'> | null;
    matter: Pick<MatterListItem, 'caseNumber' | 'matterStateTypeCode'>;
};

/**
 * A draft belongs to whoever entered it: they see it among their drafts and finalise it, so they are the one who
 * learns that the case closed under it. A timecard with no recorded creator falls back to its author.
 */
export function isOwnDraftOnClosedCase(timecard: ClosedCaseDraftTimecard, userId: number | undefined): boolean {
    const ownerId = timecard.createdByEmployee?.id ?? timecard.employee.id;
    return (
        userId !== undefined &&
        ownerId === userId &&
        timecard.timecardStatusTypeCode === TimecardStatusType.DRAFT &&
        isMatterClosed(timecard.matter.matterStateTypeCode)
    );
}

type Props = {
    timecard: ClosedCaseDraftTimecard;
    className?: string;
};

export function ClosedCaseDraftMarker({ timecard, className }: Props): JSX.Element | null {
    const { user } = useAuth();

    if (!isOwnDraftOnClosedCase(timecard, user?.id)) {
        return null;
    }

    const tooltipText = t('case-closing-draft-marker.tooltip', { case: timecard.matter.caseNumber });

    return (
        <Tooltip className="w-fit text-center" content={tooltipText}>
            <div className={cn('w-fit', className)}>
                <StatusChip
                    role="img"
                    aria-label={tooltipText}
                    icon={<PriorityHighRoundedIcon className="text-fg-error size-4" />}
                    type="error"
                />
            </div>
        </Tooltip>
    );
}
